import { MutationHookOptions } from '@apollo/client';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { cloneDeep, first, isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { Button } from '@/components/form/baseInputs/Button';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { UnsavedChangesConfirmationModal } from '@/components/modals/ConfirmationModal/UnsavedChangesConfirmationModal';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { Footer, FOOTER_HEIGHT } from '@/components/navigation/Footer';
import { Header } from '@/components/navigation/Header';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useViewOnly } from '@/contexts/InteractionParadigm.context';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { COLORS } from '@/styles/tokens/colors';
import { getNodes } from '@/utils/graphqlUtils';

import { useGrowthProfileModalQuery } from './graphql/GrowthProfileModal.generated';
import {
  useCreateGrowthProfileMutation,
  useUpdateGrowthProfileMutation,
} from './graphql/GrowthProfileModalMutations.generated';
import { GrowthProfileModalBody } from './GrowthProfileModal.components';
import { GrowthProfileModalContextProvider } from './GrowthProfileModal.context';
import {
  getDefaultGrowthProfileModalFormShape,
  GrowthProfileModalFormShape,
} from './GrowthProfileModal.types';
import {
  mapFormDataToCreateGrowthProfileInput,
  mapFormDataToUpdateGrowthProfileInput,
  mapIncomingDataToForm,
} from './GrowthProfileModal.utils';

const useStyles = makeStyles()({
  taxDragHeader: {
    backgroundColor: COLORS.GRAY[50],
  },
});

/**
 * Surfaces that open the editor on their own, i.e. not nested inside the growth
 * profiles list trowser. The scope is part of the trowser `target` because
 * several of these surfaces can be mounted at the same time and can point at
 * the same profile — an unscoped target would open all of their editors from a
 * single click.
 */
export enum GrowthProfileEditorScope {
  CashflowsAddMenu = 'cashflowsAddMenu',
  PresentationBundleSettings = 'presentationBundleSettings',
  SaleLoanIllustration = 'saleLoanIllustration',
  WaterfallAssumptions = 'waterfallAssumptions',
  WaterfallComparison = 'waterfallComparison',
}

/**
 * The {@link TrowserKey.GrowthProfile} `target`: which surface opened the editor
 * and which profile it edits, or `new` when creating one. Hosts build this for
 * both opening and for recognizing their own open state, so no parsing is
 * needed. An empty id counts as creating — the assumptions panel clears its
 * selection to start a new profile.
 */
export function growthProfileEditorTarget(
  scope: GrowthProfileEditorScope,
  growthProfileId?: string | null
): string {
  return `${scope}:${growthProfileId || 'new'}`;
}

export interface GrowthProfileModalProps {
  householdId: string;
  growthProfileId?: string | null | undefined;
  onClose: () => void;
  onAfterSave?: () => void;
  isDuplicating?: boolean | null | undefined;
  refetchQueries?: MutationHookOptions['refetchQueries'];
}

/**
 * Full-screen editor for one growth profile. Hosts render it only while it is
 * open: the standalone entry points drive that from the URL
 * ({@link TrowserKey.GrowthProfile}) so the editor deep-links and closes on
 * browser Back; the copies nested in the list trowser keep local state and
 * close with their parent.
 */
export function GrowthProfileModal({
  householdId,
  growthProfileId,
  onClose,
  onAfterSave,
  isDuplicating,
  refetchQueries,
}: GrowthProfileModalProps) {
  const { classes } = useStyles();
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();
  const canEdit = useCanEditHouseholdData();
  const isViewOnly = useViewOnly();
  const isIncomeTaxEnabled = useFeatureFlag('trust_income_tax');
  const [isEditingTaxDrag, setIsEditingTaxDrag] = useState(false);
  const [showTaxDragCancelConfirmation, setShowTaxDragCancelConfirmation] =
    useState(false);
  const taxDragSnapshot = useRef<
    GrowthProfileModalFormShape['taxDrag'] | undefined
  >(undefined);

  const primaryButtonLabel = useMemo(() => {
    if (isDuplicating) return 'Duplicate profile';
    if (growthProfileId) return 'Save profile';
    return 'Create profile';
  }, [growthProfileId, isDuplicating]);

  const modalTitle = useMemo(() => {
    if (isDuplicating) return 'Duplicate growth profile';
    if (growthProfileId) return 'Edit growth profile';
    return 'Create growth profile';
  }, [growthProfileId, isDuplicating]);

  const { data, loading: loadingDataFetch } = useGrowthProfileModalQuery({
    variables: {
      householdId,
      growthProfileId: growthProfileId ?? '',
      hasExistingGrowthProfile: !!growthProfileId,
    },
    fetchPolicy: 'network-only', // always get the latest data
  });

  const [createGrowthProfile, { loading: loadingCreateGrowthProfile }] =
    useCreateGrowthProfileMutation({
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted() {
        onAfterSave?.();
        showFeedback('Growth profile created successfully', {
          variant: 'success',
        });
        trackUserEvent('growth_profile created');
      },
    });

  const [updateGrowthProfile, { loading: loadingUpdateGrowthProfile }] =
    useUpdateGrowthProfileMutation({
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted() {
        onAfterSave?.();
        showFeedback('Growth profile saved successfully', {
          variant: 'success',
        });
        trackUserEvent('growth_profile updated');
      },
    });

  const loading =
    loadingDataFetch ||
    loadingCreateGrowthProfile ||
    loadingUpdateGrowthProfile;
  const missingTaxDragDefaults =
    isIncomeTaxEnabled && !data?.incomeTaxDragDefaults;

  const formMethods = useForm<GrowthProfileModalFormShape>({
    defaultValues: getDefaultGrowthProfileModalFormShape(),
  });

  const { reset, handleSubmit, getValues, setValue } = formMethods;

  const existingGrowthProfile = useMemo(
    () => first(getNodes(data?.growthProfiles)),
    [data?.growthProfiles]
  );

  const assetClasses = useMemo(
    () => getNodes(data?.assetClasses),
    [data?.assetClasses]
  );

  const primaryClientState = useMemo(
    () =>
      first(getNodes(data?.households))?.possiblePrimaryClients.find(
        ({ isPrimary }) => isPrimary
      )?.address?.stateCode,
    [data?.households]
  );

  useEffect(() => {
    if (
      loadingDataFetch ||
      missingTaxDragDefaults ||
      formMethods.formState.isDirty
    )
      return;
    reset(
      mapIncomingDataToForm(
        existingGrowthProfile,
        assetClasses,
        primaryClientState,
        data?.incomeTaxDragDefaults
      )
    );
  }, [
    existingGrowthProfile,
    reset,
    loadingDataFetch,
    assetClasses,
    primaryClientState,
    data?.incomeTaxDragDefaults,
    missingTaxDragDefaults,
    formMethods.formState.isDirty,
  ]);

  const growthProfile = useMemo(
    () => first(getNodes(data?.growthProfiles)),
    [data?.growthProfiles]
  );

  const isDefault = growthProfile?.isDefault ?? false;

  const openTaxDragEditor = () => {
    taxDragSnapshot.current = cloneDeep(getValues('taxDrag'));
    setIsEditingTaxDrag(true);
  };

  const closeTaxDragEditor = () => {
    setIsEditingTaxDrag(false);
    taxDragSnapshot.current = undefined;
  };

  const cancelTaxDragEditor = () => {
    const snapshot = taxDragSnapshot.current;
    if (snapshot && !isEqual(snapshot, getValues('taxDrag'))) {
      setShowTaxDragCancelConfirmation(true);
      return;
    }
    closeTaxDragEditor();
  };

  // The profile fields are unmounted in this view. Submit validates the mounted
  // tax fields and exposes their errors without sending a mutation.
  const finishTaxDragEditor = handleSubmit(closeTaxDragEditor);

  const handleTaxDragCancelConfirmation = (reason: 'confirm' | 'cancel') => {
    setShowTaxDragCancelConfirmation(false);
    if (reason === 'confirm') return;

    const snapshot = taxDragSnapshot.current;
    if (snapshot) {
      setValue('taxDrag', snapshot, { shouldDirty: true });
    }
    closeTaxDragEditor();
  };

  const onSubmit = handleSubmit(async (data: GrowthProfileModalFormShape) => {
    try {
      if (growthProfileId && !isDuplicating) {
        await updateGrowthProfile({
          variables: mapFormDataToUpdateGrowthProfileInput(
            data,
            householdId,
            growthProfileId,
            isIncomeTaxEnabled
          ),
        });
      } else {
        await createGrowthProfile({
          variables: mapFormDataToCreateGrowthProfileInput(
            data,
            householdId,
            isIncomeTaxEnabled
          ),
        });
      }
      onClose();
    } catch (e) {
      reportError('Error submitting growth profile', e as Error);
      let prefix = 'Error creating growth profile.';
      if (isDuplicating) {
        prefix = 'Error duplicating growth profile.';
      } else if (growthProfileId) {
        prefix = 'Error updating growth profile.';
      }

      showFeedback(`${prefix}  Please try again.`, { variant: 'error' });
    }
  });

  return (
    <GrowthProfileModalContextProvider
      growthProfileId={growthProfileId ?? null}
      isDefault={isDefault}
      isDuplicating={isDuplicating ?? false}
      assetClasses={assetClasses}
      householdId={householdId}
      taxDragDefaults={data?.incomeTaxDragDefaults}
    >
      <FormProvider {...formMethods}>
        <HouseholdPermissionAwareDisabledProvider
          forceDisabled={loading || missingTaxDragDefaults || isViewOnly}
        >
          <FullScreenModal
            trowserKey={TrowserKey.GrowthProfile}
            isOpen
            onClose={onClose}
            data-testid="growth-profile-modal"
          >
            {isIncomeTaxEnabled && (
              <Header
                classes={
                  isEditingTaxDrag ? { root: classes.taxDragHeader } : undefined
                }
                leftHeaderContent={
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto 1fr',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Button
                      variant="transparent"
                      size="sm"
                      startIcon={isEditingTaxDrag ? undefined : ChevronLeftIcon}
                      onClick={isEditingTaxDrag ? cancelTaxDragEditor : onClose}
                      disabled={loading}
                      sx={{ justifySelf: 'start' }}
                      data-testid="growth-profile-modal-cancel-button"
                    >
                      {isEditingTaxDrag && 'Cancel'}
                      {!isEditingTaxDrag &&
                        (canEdit && !isViewOnly
                          ? 'Exit without saving'
                          : 'Close')}
                    </Button>
                    <Typography variant="title-s">
                      {isEditingTaxDrag
                        ? 'Edit growth profile: Tax drag'
                        : modalTitle}
                    </Typography>
                    <Box sx={{ justifySelf: 'end' }}>
                      {(isEditingTaxDrag || (canEdit && !isViewOnly)) && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={
                            isEditingTaxDrag ? finishTaxDragEditor : onSubmit
                          }
                          loading={loading}
                          disabled={loading || missingTaxDragDefaults}
                          data-testid={
                            isEditingTaxDrag
                              ? 'tax-drag-editor-done-button'
                              : 'growth-profile-modal-submit-button'
                          }
                        >
                          Done
                        </Button>
                      )}
                    </Box>
                  </Box>
                }
              />
            )}
            <Stack
              height="100%"
              pb={isIncomeTaxEnabled ? 0 : `${FOOTER_HEIGHT}px`}
              sx={{
                overflowY: 'auto',
                minHeight: 0,
                backgroundColor: isIncomeTaxEnabled
                  ? 'background.paper'
                  : COLORS.GRAY[50],
              }}
              width="100%"
              alignItems="center"
            >
              <LoadingOverlay open={loading} />
              {!loadingDataFetch && missingTaxDragDefaults && (
                <Alert severity="error">
                  Tax rate defaults could not be loaded. Close and reopen this
                  profile to try again.
                </Alert>
              )}
              <Box
                maxWidth={isEditingTaxDrag ? undefined : '800px'}
                width={isIncomeTaxEnabled ? '100%' : undefined}
              >
                <GrowthProfileModalBody
                  isEditingTaxDrag={isEditingTaxDrag}
                  onEditTaxDrag={openTaxDragEditor}
                />
              </Box>
            </Stack>
            {!isIncomeTaxEnabled && (
              <Footer
                rightAction={
                  <Stack direction="row" spacing={1}>
                    <Button
                      key="cancel"
                      variant="secondary"
                      size="sm"
                      onClick={isEditingTaxDrag ? cancelTaxDragEditor : onClose}
                      disabled={
                        loadingCreateGrowthProfile || loadingUpdateGrowthProfile
                      }
                      loading={loading}
                      data-testid="growth-profile-modal-cancel-button"
                    >
                      {isEditingTaxDrag || (canEdit && !isViewOnly)
                        ? 'Cancel'
                        : 'Close'}
                    </Button>
                    {isEditingTaxDrag && (
                      <Button
                        key="done"
                        variant="primary"
                        size="sm"
                        onClick={finishTaxDragEditor}
                        data-testid="tax-drag-editor-done-button"
                      >
                        Done
                      </Button>
                    )}
                    {canEdit && !isViewOnly && !isEditingTaxDrag && (
                      <Button
                        key="submit"
                        variant="primary"
                        size="sm"
                        onClick={onSubmit}
                        loading={loading}
                        disabled={loading || missingTaxDragDefaults}
                        data-testid="growth-profile-modal-submit-button"
                      >
                        {primaryButtonLabel}
                      </Button>
                    )}
                  </Stack>
                }
              />
            )}
            {showTaxDragCancelConfirmation && (
              <UnsavedChangesConfirmationModal
                isOpen
                onClose={handleTaxDragCancelConfirmation}
              />
            )}
          </FullScreenModal>
        </HouseholdPermissionAwareDisabledProvider>
      </FormProvider>
    </GrowthProfileModalContextProvider>
  );
}
